import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { vectorStore, isUsingSupabase } from '@/lib/ai/vector-store-provider';
import { useToast } from '@/hooks/use-toast';
import { Upload, FileText, Check, AlertTriangle, Cloud, HardDrive } from 'lucide-react';

interface TrainingDataUploaderProps {
  onComplete: () => void;
}

export function TrainingDataUploader({ onComplete }: TrainingDataUploaderProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setIsLoading(true);
    setProgress(10);

    try {
      // Read the file as text
      const text = await readFileAsText(file, (p) => {
        setProgress(10 + p * 40); // 10-50% progress for file reading
      });

      setProgress(60);

      // Save the training data to localStorage for future sessions
      try {
        localStorage.setItem('flexhelp-training-data', text);
        localStorage.setItem('has-training-data', 'true');
        console.log('Training data saved to localStorage');
      } catch (storageError) {
        console.warn('Failed to save training data to localStorage:', storageError);
      }

      // Process the training data
      await vectorStore.loadTrainingData(text);
      
      setProgress(100);
      
      toast({
        title: "Training data uploaded",
        description: `Successfully processed ${vectorStore.getDocumentCount()} document chunks.`,
      });
      
      onComplete();
    } catch (error) {
      console.error('Error processing training data:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to upload training data. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const readFileAsText = (file: File, onProgress: (progress: number) => void): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = event.loaded / event.total;
          onProgress(progress);
        }
      };
      
      reader.onload = (event) => {
        resolve(event.target?.result as string);
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsText(file);
    });
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Upload Training Data</CardTitle>
        <CardDescription>
          Upload text files containing knowledge that FlexHelp AI should use when answering questions.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div 
          className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={triggerFileInput}
        >
          <input
            type="file"
            ref={fileInputRef}
            accept=".txt,.md,.csv,.json"
            className="hidden"
            onChange={handleFileSelect}
            disabled={isLoading}
          />
          
          {fileName ? (
            <div className="flex flex-col items-center justify-center">
              <FileText className="h-12 w-12 text-muted-foreground mb-2" />
              <p className="text-sm font-medium mb-1">{fileName}</p>
              {isLoading ? (
                <p className="text-xs text-muted-foreground">Processing...</p>
              ) : (
                <p className="text-xs text-muted-foreground">File selected</p>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center">
              <Upload className="h-12 w-12 text-muted-foreground mb-2" />
              <p className="text-sm font-medium mb-1">Click to upload or drag and drop</p>
              <p className="text-xs text-muted-foreground">Supported formats: TXT, MD, CSV, JSON</p>
            </div>
          )}
        </div>
        
        {isLoading && (
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span>Processing</span>
              <span>{progress.toFixed(0)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}
        
        {vectorStore.isReady() && (
          <div className="flex items-center gap-2 text-sm bg-primary/10 rounded-lg p-3">
            <Check className="h-4 w-4 text-green-500" />
            <span>{vectorStore.getDocumentCount()} document chunks ready for use</span>
          </div>
        )}
        
        <div className="flex items-center gap-2 text-xs text-muted-foreground border rounded-lg p-2">
          {isUsingSupabase() ? (
            <>
              <Cloud className="h-3 w-3 text-blue-500" />
              <span>Using Supabase for persistent storage</span>
            </>
          ) : (
            <>
              <HardDrive className="h-3 w-3 text-orange-500" />
              <span>Using in-memory storage (data will be lost on refresh)</span>
            </>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        {vectorStore.isReady() ? (
          <Button onClick={onComplete} disabled={isLoading}>
            Continue
          </Button>
        ) : (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertTriangle className="h-4 w-4" />
            <span>Upload training data to continue</span>
          </div>
        )}
      </CardFooter>
    </Card>
  );
} 